<template>
  <ModalBackdrop>
    <div class="fakeBackdrop" @click="cancelHandler"></div>

    <div class="fake-over"></div>

    <div class="container" ref="mainContainer">
      <div
        class="bar"
        ref="bar"
        draggable="true"
        @dragstart="dragStartHandler"
        @drag="modifyHeight"
        @dragend="endModifyHeight"
        @touchmove="modifyHeight"
        @touchend="endModifyHeight"
      ></div>

      <!-- Content -->
      <div
        class="content list"
        ref="listContainer"
        @scroll="loadMoreForInfiniteScroll"
      >
        <CommentList
          :post="post"
          :model="model"
          ref="list"
          @reply="replyHandler"
          @changeHeight="changeListHeightHandler"
        />
      </div>

      <!-- Input -->
      <div class="content input" ref="inputBox">
        <!-- <div> -->
        <CommentInput
          :post="post"
          ref="input"
          @sendMessage="sendMessage"
          @cancelReply="cancelReply"
        />
        <!-- </div> -->
      </div>
    </div>
  </ModalBackdrop>
</template>

<script setup lang="ts">
import CommentList from "./List.vue";
import CommentInput from "./Input.vue";
import { Post } from "~/types/Post";
import { Model } from "~/types/Model";

const { post, model } = defineProps<{
  post: Post;
  model: Model;
}>();

const $emit = defineEmits<{
  (event: "cancel"): void;
}>();

// Data
let containerAnimationTimeout: NodeJS.Timeout;
let actualHeight: null | number = null;
let previousHeight: null | number = null;
let lastScrollTop = 0;
let replyCallback: Function | null = null;
const maxHeight = window.innerHeight;

// Refs
const mainContainer = ref<HTMLElement>();
const list = ref<typeof CommentList>();
const input = ref<typeof CommentInput>();
const bar = ref<HTMLElement>();
const inputBox = ref<HTMLElement>();
const listContainer = ref<HTMLElement>();

// Methods
function dragStartHandler(e: DragEvent) {
  e.dataTransfer!.setDragImage(new Image(), 0, 0);
}

function replyHandler(data: { comment: Comment; cb: Function }) {
  input.value!.replyTo(data.comment);
  replyCallback = data.cb;
}

function cancelReply() {
  replyCallback = null;
}

function loadMoreForInfiniteScroll(e: Event) {
  const element = e.target! as HTMLElement;

  if (element.scrollTop < lastScrollTop) return;

  lastScrollTop = element.scrollTop <= 0 ? 0 : element.scrollTop;

  if (element.scrollTop + element.offsetHeight >= element.scrollHeight) {
    list.value!.endOfScrollReached();
  }
}

function cancelHandler() {
  mainContainer.value!.style.transition =
    "transform 0.2s ease-in-out, height 0.1s ease-in-out;";

  mainContainer.value!.style.height = "0%";
  mainContainer.value!.style.transform = `translateY(200px)`;

  containerAnimationTimeout = setTimeout(() => {
    $emit("cancel");
  }, 200);
}

function changeListHeightHandler(value: number) {
  restoreHeightTransition();

  const barHeight = bar.value!.clientHeight;
  const inputBoxHeight = inputBox.value!.clientHeight;

  mainContainer.value!.style.height = `${
    value + barHeight + inputBoxHeight + 10
  }px`;
}

function restoreHeightTransition() {
  mainContainer.value!.style.transition = "height 0.25s ease-in-out";
}

function cancelHeightTransition() {
  mainContainer.value!.style.transition = "none";
}

function modifyHeight(e: TouchEvent | MouseEvent) {
  cancelHeightTransition();

  // @ts-ignore
  let clientY: number = e.clientY;

  if (e.type !== "drag") {
    // @ts-ignore
    clientY = e.touches[0].clientY;
    // @ts-ignore
  } else if (e.x === 0 && e.y === 0) return;

  if (actualHeight !== null) {
    previousHeight = actualHeight;
  }

  actualHeight = maxHeight - clientY;
  mainContainer.value!.style.height = `${actualHeight}px`;
}

function endModifyHeight(e: DragEvent | TouchEvent) {
  restoreHeightTransition();
  if (previousHeight! > actualHeight!) {
    cancelHandler();
  } else {
    mainContainer.value!.style.height = "100%";
  }
}

async function sendMessage(data: string) {
  if (!replyCallback) {
    list.value!.appendComment(data);
    // listContainer.scrollTop = 0;
    listContainer.value!.scrollTo({ top: 0, behavior: "smooth" });
    return;
  }

  replyCallback(data);
  replyCallback = null;
}

onMounted(() => {
  mainContainer.value!.style.transform = `translateY(0px)`;
});

onBeforeUnmount(() => {
  if (containerAnimationTimeout) {
    clearTimeout(containerAnimationTimeout);
  }
});
</script>

<style lang="scss" scoped>
@import "@/assets/scss/variables";
@import "@/assets/scss/classes";

.fake-over {
  width: 100%;
  height: auto;

  background-color: transparent;

  display: flex;
  flex-grow: 1;
}

.container {
  width: 100%;
  height: 300px;
  max-height: 100%;
  margin-top: auto;
  margin-bottom: 0;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;

  position: relative;
  z-index: 99;

  transform: translateY(100%);

  transition: transform 0.25s ease-in-out;

  > .bar {
    width: 100%;
    height: 3em;
    background-color: var(--pallete_dark_color);

    border-radius: 10px;
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
    box-shadow: 0 0 1rem rgba(0, 0, 0, 0.25);

    border-bottom: 1px solid rgba($pallete_dark_text_color, 0.25);

    cursor: grab;

    &::after {
      position: relative;
      display: flex;
      content: "";
      width: 5%;
      height: 5px;
      margin-block: 0.75em 1.25em;
      margin-inline: auto;
      left: 0;
      right: 0;

      background-color: var(--pallete_dark_text_color);

      border-radius: 5px;

      box-shadow: 0 0 1rem rgba(0, 0, 0, 0.25);
    }
  }

  > .content {
    width: 100%;
    background-color: var(--pallete_dark_color);
    display: flex;
    padding-top: 1em;

    // flex-grow: 1;
    overflow: scroll;

    &::-webkit-scrollbar {
      display: none;
    }

    &.list {
      flex-grow: 1;
      padding-bottom: 7em;
    }

    &.input {
      width: 100%;
      padding: 0;
      padding-block: 0 0.5em;

      position: absolute;
      bottom: 0;
      left: 0;
      z-index: 999;
    }
  }
}

.fakeBackdrop {
  width: 100%;
  height: 100%;
  background-color: transparent;
  display: flex;
  justify-content: center;
  align-items: flex-end;

  position: absolute;
  z-index: 0;
}
</style>
